/* istanbul ignore file */

import React, { memo, useCallback, useEffect, useState } from 'react';
import { BlurView } from 'expo-blur';
import { useNavigation, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { type LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import {
  CustomNavigationHeader,
  GlassView,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';

import { useIsFoldableDevice, useIsLiquidGlassEnabled } from '../../../../hooks';
import { useLocationSearchFlags } from '../../hooks/useLocationSearchFlags';
import { type LocationSearchService } from '../../machine';
import { LocationResultsListsContainer, LocationResultsListsV2 } from '../LocationResults';
import {
  LOCATION_RESULTS_BOTTOM_MARGIN_XS_UPDATE,
  LOCATION_SELECTION_UI_BOX_SHADOW_XS,
} from '../LocationResults/LocationResults.constants';
import { LocationSearchFieldForSearchType } from '../LocationSearchFieldForSearchType';
import { LocationSearchHeader } from '../LocationSearchHeader';
import { LocationSearchMap } from '../LocationSearchMap';
import { LocationSearchResults } from '../LocationSearchResults';
import { LocationSearchTypePicker } from '../LocationSearchTypePicker';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchContent = memo((props: LocationSearchContentProps) => {
  const { match } = useResponsive();

  const ResponsiveComponent = match([LocationSearchContentXS, LocationSearchContentSM]);

  return <ResponsiveComponent {...props} />;
});

// ─── Responsive Components ───────────────────────────────────────────────────

const LocationSearchContentXS = memo((props: LocationSearchContentProps) => {
  const {
    locationSearchActorRef,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    isInsideModal,
  } = props;

  const navigation = useNavigation();
  const { height: navbarContainerHeight } = useNavbarContainerHeight();
  const { right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();
  const { shouldRenderRecentAndNearbyLocations } = useLocationSearchFlags({
    locationSearchActorRef,
  });
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();
  const isFoldableDevice = useIsFoldableDevice();
  const navbarOffsetStyles = useStyle(
    () => ({ bottom: navbarContainerHeight + theme.spacing['4'] }),
    [navbarContainerHeight],
  );

  const setOptions = navigation.setOptions;

  // ─── State ──────────────────────────────────────────────────────────

  const [isLocationResultsExpanded, setIsLocationResultsExpanded] = useState(true);
  const [locationHeaderHeight, setLocationHeaderHeight] = useState(0);

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldOffsetContentForNavbar = !isInsideModal;
  const hasHorizontalSafeArea = safeAreaRight > 0 || safeAreaLeft > 0;
  const shouldApplyHeaderTopSafeArea = !isInsideModal || isFoldableDevice;
  const shouldShowExpandedLocationContainer = isLocationResultsExpanded;
  const shouldShowCollapsedLocationHeader = !isLocationResultsExpanded;
  const shouldAddModalBottomMargin = shouldShowExpandedLocationContainer && isInsideModal;
  const locationContainerPointerEvents = shouldShowCollapsedLocationHeader ? 'box-none' : 'auto';
  const mapTopInset = locationHeaderHeight > 0 ? locationHeaderHeight : undefined;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleLocationHeaderLayout = useCallback((event: LayoutChangeEvent) => {
    setLocationHeaderHeight(event.nativeEvent.layout.height);
  }, []);

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerStyles = [
    styles.contentContainerXS,
    styles.contentContainerXSUpdate,
    shouldOffsetContentForNavbar ? navbarOffsetStyles : undefined,
  ];
  const headerContainerDynamicStyles = useStyle(
    () => ({
      paddingTop: isFoldableDevice ? theme.spacing['5'] : undefined,
      paddingBottom: hasHorizontalSafeArea ? theme.spacing['5'] : undefined,
      paddingRight: safeAreaRight,
      paddingLeft: safeAreaLeft,
    }),
    [hasHorizontalSafeArea, isFoldableDevice, safeAreaLeft, safeAreaRight],
  );

  const headerContainerStyles = [
    styles.headerContainerXS,
    styles.headerContainerXSUpdate,
    headerContainerDynamicStyles,
  ];
  const locationsContainerStyles = [
    styles.locationsContainerXS,
    styles.locationsContainerXSUpdate,
    shouldShowExpandedLocationContainer ? styles.locationsContainerXSUpdateExpanded : undefined,
    shouldAddModalBottomMargin ? styles.locationsContainerXSUpdateModalBottomSpacing : undefined,
  ];
  const locationsContainerHeaderStyles = styles.locationsContainerHeaderXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  // We use a custom navigation header on XS breakpoint.
  useEffect(() => {
    setOptions({ headerShown: false });
  }, [setOptions]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.containerXS}>
      <LocationSearchMap
        locationSearchActorRef={locationSearchActorRef}
        topOverlayInset={mapTopInset}
      />

      <View style={contentContainerStyles} pointerEvents={locationContainerPointerEvents}>
        <View style={headerContainerStyles} onLayout={handleLocationHeaderLayout}>
          {isLiquidGlassEnabled ? (
            <GlassView
              glassEffectStyle="regular"
              style={styles.headerContainerBlurXS}
              pointerEvents="none"
            />
          ) : null}

          {!isLiquidGlassEnabled ? (
            <BlurView
              intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
              tint="light"
              style={styles.headerContainerBlurXS}
              pointerEvents="none"
            />
          ) : null}

          <LocationSearchHeader
            shouldApplySafeAreaInsets={shouldApplyHeaderTopSafeArea}
            locationSearchActorRef={locationSearchActorRef}
          />

          <View style={locationsContainerHeaderStyles}>
            <LocationSearchFieldForSearchType locationSearchActorRef={locationSearchActorRef} />
          </View>
        </View>

        <View style={locationsContainerStyles} pointerEvents={locationContainerPointerEvents}>
          {shouldShowExpandedLocationContainer ? (
            <View style={styles.locationContainerBackground} pointerEvents="none">
              {isLiquidGlassEnabled ? (
                <GlassView glassEffectStyle="regular" style={StyleSheet.absoluteFill} />
              ) : (
                <BlurView
                  intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
                  tint="light"
                  style={StyleSheet.absoluteFill}
                />
              )}
            </View>
          ) : null}

          {shouldRenderRecentAndNearbyLocations ? (
            <LocationResultsListsContainer>
              <LocationResultsListsV2.XS
                locationSearchActorRef={locationSearchActorRef}
                variation="recent-and-nearby"
                isExpandedByDefault
                withSafeAreaInsets={isInsideModal}
                interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
                interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
                onExpandedChange={setIsLocationResultsExpanded}
              />
            </LocationResultsListsContainer>
          ) : (
            <LocationSearchResults
              locationSearchActorRef={locationSearchActorRef}
              interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
              interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
              withSafeAreaInsets={isInsideModal}
              onExpandedChange={setIsLocationResultsExpanded}
            />
          )}
        </View>
      </View>
    </View>
  );
});

const LocationSearchContentSM = memo((props: LocationSearchContentProps) => {
  const {
    locationSearchActorRef,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    isInsideModal,
    withNavHeader,
    onNavClose,
  } = props;

  const router = useRouter();
  const navigation = useNavigation();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  const { shouldRenderRecentAndNearbyLocations } = useLocationSearchFlags({
    locationSearchActorRef,
  });

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleNavClose = useCallback(() => {
    const canGoBack = router.canGoBack() ?? false;

    if (canGoBack) {
      router.back();
      return;
    }

    router.navigate('/');
  }, [router]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const headerStyles = withNavHeader ? undefined : styles.locationsContainerHeaderSM;
  const locationsContainerStyles = [
    styles.locationsContainerSM,
    styles.locationsContainerSMUpdate,
    currentBreakpoint.isSM ? styles.locationsContainerNarrowSM : undefined,
  ];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    navigation.setOptions({ headerShown: false, headerTitle: null });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.screenContainerSM}>
      <View style={styles.contentContainerSM}>
        <LocationSearchMap locationSearchActorRef={locationSearchActorRef} />

        <View style={locationsContainerStyles}>
          {isLiquidGlassEnabled ? (
            <GlassView
              glassEffectStyle="regular"
              style={styles.locationsContainerBlurSM}
              pointerEvents="none"
            />
          ) : null}

          {!isLiquidGlassEnabled ? (
            <BlurView
              intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
              tint="light"
              style={styles.locationsContainerBlurSM}
              pointerEvents="none"
            />
          ) : null}

          <View style={headerStyles}>
            {withNavHeader ? (
              <CustomNavigationHeader.Container
                palette="cream"
                withBackground={false}
                buttonsReservedSpace="left-and-right"
                safeAreaEdges={null}
              >
                <CustomNavigationHeader.Text sizeMatch={['22']}>
                  <FormattedMessage {...messages.navTitle} />
                </CustomNavigationHeader.Text>

                <CustomNavigationHeader.Button.Right
                  name="IconClose"
                  accessibilityLabel={formatMessage(messages.navCloseButtonLabel)} // prettier-ignore
                  onPress={onNavClose ?? handleNavClose}
                />
              </CustomNavigationHeader.Container>
            ) : null}

            <View style={styles.locationSearchTypePickerContainer}>
              <View>
                <LocationSearchTypePicker locationSearchActorRef={locationSearchActorRef} />
              </View>
            </View>

            <LocationSearchFieldForSearchType locationSearchActorRef={locationSearchActorRef} />
          </View>

          {shouldRenderRecentAndNearbyLocations ? (
            <LocationResultsListsContainer>
              <LocationResultsListsV2.SM
                locationSearchActorRef={locationSearchActorRef}
                variation="recent-and-nearby"
                interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
                interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
              />
            </LocationResultsListsContainer>
          ) : (
            <LocationSearchResults
              locationSearchActorRef={locationSearchActorRef}
              interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
              interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
              withSafeAreaInsets={isInsideModal}
            />
          )}
        </View>
      </View>
    </View>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_CONTAINER_BLUR_INTENSITY = 85;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screenContainerSM: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentContainerSM: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentContainerXS: {
    flex: 1,
    zIndex: 1,
  },
  contentContainerXSUpdate: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: theme.spacing['4'],
    left: 0,
  },
  headerContainerXS: {
    zIndex: 2,
  },
  headerContainerXSUpdate: {
    borderBottomLeftRadius: theme.radius.xlarge,
    borderBottomRightRadius: theme.radius.xlarge,
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
    boxShadow: LOCATION_SELECTION_UI_BOX_SHADOW_XS,
    paddingBottom: theme.spacing['3'],
    overflow: 'hidden',
  },
  headerContainerBlurXS: {
    ...StyleSheet.absoluteFill,
    borderBottomLeftRadius: theme.radius.xlarge,
    borderBottomRightRadius: theme.radius.xlarge,
    overflow: 'hidden',
  },
  locationContainerBackground: {
    ...StyleSheet.absoluteFill,
  },
  locationsContainerXS: {
    flex: 1,
    zIndex: 1,
  },
  locationsContainerXSUpdate: {
    marginTop: theme.spacing['4'],
    marginHorizontal: theme.spacing['4'],
  },
  locationsContainerXSUpdateExpanded: {
    borderRadius: theme.radius.xlarge,
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
    boxShadow: LOCATION_SELECTION_UI_BOX_SHADOW_XS,
    overflow: 'hidden',
  },
  locationsContainerXSUpdateModalBottomSpacing: {
    marginBottom: LOCATION_RESULTS_BOTTOM_MARGIN_XS_UPDATE,
  },
  locationsContainerSM: {
    width: 375,
    flexGrow: 0,
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
  locationsContainerNarrowSM: {
    width: 340,
  },
  locationsContainerSMUpdate: {
    position: 'absolute',
    top: theme.spacing['3'],
    right: theme.spacing['3'],
    bottom: theme.spacing['3'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
    boxShadow: '0px 4px 30px 10px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
    borderLeftWidth: 0,
  },
  locationsContainerBlurSM: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.xlarge,
    overflow: 'hidden',
  },
  locationsContainerHeaderXS: {
    zIndex: 2,
  },
  locationsContainerHeaderSM: {
    paddingTop: theme.spacing['6'],
  },
  locationSearchTypePickerContainer: {
    alignItems: 'center',
    paddingBottom: theme.spacing['4'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  navTitle: {
    defaultMessage: 'Select your location',
    description: 'Location search | Navigation header | Title',
  },
  navCloseButtonLabel: {
    defaultMessage: 'Close',
    description: 'Location search | Navigation close button | Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchContentProps = {
  locationSearchActorRef: LocationSearchService;
  interactedLocationRestaurantSlug: string | undefined;
  interactedLocationDeliveryAddressId: string | undefined;
  isInsideModal?: boolean;
  withNavHeader?: boolean;
  onNavClose?: () => void;
};
